import SearchApi from "js-worker-search";
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
  type FC,
} from "react";
import { useAccountList, type Account } from "../../hooks";

const initSearchApi = () =>
  new SearchApi({
    caseSensitive: false,
  });

export interface AccountNode {
  isExpand: boolean;
  account: Account;
  level: number;
}

type FlattenAccountList = Record<
  string,
  Omit<Account, "children" | "accountName"> & {
    accountName: string[];
    normalizeAccountName: string[];
  }
>;
export type AccountSwitcherContextValue = {
  currentAccount: Account;
  flattenAccountList: FlattenAccountList;
  hasAtLeastOneAccountHasChildren: boolean;
  hasOnlyOneAccount: boolean;
  isSearchBarFocus: boolean;
  list: AccountNode[];
  onAccountChange: (_: {
    accountId: Account["accountId"];
    accountName: Account["accountName"];
  }) => unknown;
  searchAccounts: string[];
  searchValue: string;
  setList: Dispatch<SetStateAction<AccountNode[]>>;
  setSearchBarFocus: (_: boolean) => void;
  setSearchValue: (_: string) => void;
  userName: string;
};

const AccountSwitcherContext = createContext<
  AccountSwitcherContextValue | undefined
>(undefined);

export interface AccountSwitcherProviderProps {
  children?: ReactNode;
  userName: AccountSwitcherContextValue["userName"];
  currentAccount: AccountSwitcherContextValue["currentAccount"];
  onAccountChange: AccountSwitcherContextValue["onAccountChange"];
  rights: string[] | undefined;
}
export const AccountSwitcherProvider: FC<AccountSwitcherProviderProps> = ({
  children,
  userName,
  rights,
  currentAccount,
  onAccountChange,
}) => {
  const accountList = useAccountList(currentAccount, rights);
  const [list, setList] = useState<AccountNode[]>([]);
  const searchApi = useRef<SearchApi>(initSearchApi());
  const [searchValue, setSearchValue] =
    useState<AccountSwitcherContextValue["searchValue"]>("");
  const [searchAccounts, setSearchAccounts] = useState<
    AccountSwitcherContextValue["searchAccounts"]
  >([]);
  const [isSearchBarFocus, setSearchBarFocus] = useState<boolean>(false);

  const hasOnlyOneAccount =
    accountList?.length === 1 && !accountList?.[0]?.children?.length;

  const hasAtLeastOneAccountHasChildren: boolean =
    (accountList &&
      Array.isArray(accountList) &&
      accountList.some((acc) => !!acc.children?.length)) ||
    false;

  const search = useCallback(async (query: string) => {
    let result: string[] = [];
    if (query) {
      result = await searchApi.current.search(query);
    }
    setSearchAccounts(result.sort((a, b) => a.length - b.length));
  }, []);

  useEffect(() => {
    search(searchValue);
  }, [search, searchValue]);

  const flattenAccountList = useMemo<FlattenAccountList>(() => {
    /* istanbul ignore next */
    if (!accountList?.length) return {};
    searchApi.current = initSearchApi();

    const flatten = (
      list: Account[],
      parentName: string[],
      result: FlattenAccountList
    ) => {
      list.forEach((item) => {
        const account: FlattenAccountList[0] = {
          accountId: item.accountId,
          accountName: [...parentName, item.accountName || ""],
          normalizeAccountName: [
            ...parentName,
            item.accountName?.toLowerCase() || "",
          ],
        };

        const uuid = account.accountName.join(" / ");
        if (item.children?.length) {
          flatten(
            item.children,
            [...parentName, item.accountName || ""],
            result
          );
        }
        searchApi.current.indexDocument(
          uuid,
          (item.accountName || "").toLowerCase()
        );
        result[uuid] = account;
      });
    };
    const result: FlattenAccountList = {};
    flatten(accountList, [], result);

    return result;
  }, [accountList]);

  useEffect(() => {
    if (accountList && accountList?.length) {
      setList(
        accountList.map((account) => ({
          isExpand: false,
          account,
          level: 1,
        }))
      );
    }
  }, [accountList]);

  const value = {
    currentAccount,
    flattenAccountList,
    hasAtLeastOneAccountHasChildren,
    hasOnlyOneAccount,
    isSearchBarFocus,
    list,
    onAccountChange,
    searchAccounts,
    searchValue,
    setList,
    setSearchBarFocus,
    setSearchValue,
    userName,
  };

  return (
    <AccountSwitcherContext.Provider value={value}>
      {children}
    </AccountSwitcherContext.Provider>
  );
};
export const useAccountSwitcherContext = (): AccountSwitcherContextValue => {
  const context = useContext(AccountSwitcherContext);
  if (context === undefined) {
    throw new Error(
      "useAccountSwitcherContext must be used within a AccountSwitcherProvider"
    );
  }
  return context;
};
